import { Portal, Tooltip, chakra, useSlotRecipe } from '@chakra-ui/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { tooltipSlotRecipe } from '../../theme/recipes/tooltip';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

/** Above grid tiles / floating chrome; below modals & context menus (~1600+). */
const BRAND_TOOLTIP_Z_INDEX = 1500;

interface BrandTooltipProps {
  content: ReactNode;
  children: ReactNode;
  /** Override default tooltip width for longer copy (e.g. Riftboundle axis hints). */
  maxContentW?: string;
  /** Override tooltip shell radius for custom visual content. */
  contentBorderRadius?: string;
  /** Override tooltip shell horizontal padding. */
  contentPx?: string | number;
  /** Override tooltip shell vertical padding. */
  contentPy?: string | number;
  /** Clip custom visual content to the tooltip shell radius. */
  contentOverflow?: string;
  /** Floating UI placement passed to Tooltip.Root (default top). */
  placement?: TooltipPlacement;
  /** Ms before the tooltip opens on hover (default 150). */
  openDelay?: number;
  closeDelay?: number;
  /** Render in a portal so peeks sit above grid siblings (default true). */
  portal?: boolean;
  /** Stacking order for the portalled positioner. */
  zIndex?: number;
  /**
   * Hover still opens the tip. A click pins it open until the trigger is
   * clicked again, Escape, or a press outside — needed where hover never fires.
   */
  openOnClick?: boolean;
}

export function BrandTooltip({
  content,
  children,
  maxContentW,
  contentBorderRadius,
  contentPx,
  contentPy,
  contentOverflow,
  placement = 'top',
  openDelay = 150,
  closeDelay = 100,
  portal = true,
  zIndex = BRAND_TOOLTIP_Z_INDEX,
  openOnClick = false,
}: BrandTooltipProps) {
  const [open, setOpen] = useState(false);
  const pinnedRef = useRef(false);

  useEffect(() => {
    if (!openOnClick || !pinnedRef.current) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        pinnedRef.current = false;
        setOpen(false);
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-scope="tooltip"]')) return;
      pinnedRef.current = false;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [openOnClick, open]);

  const recipe = useSlotRecipe({ recipe: tooltipSlotRecipe });
  const chrome = recipe({});

  if (!content) return <>{children}</>;

  const positioner = (
    <Tooltip.Positioner zIndex={zIndex}>
      <Tooltip.Content
        maxW={maxContentW ?? '260px'}
        borderRadius={contentBorderRadius}
        px={contentPx}
        py={contentPy}
        overflow={contentOverflow}
        css={chrome.content}
      >
        <Tooltip.Arrow>
          <Tooltip.ArrowTip css={chrome.arrowTip} />
        </Tooltip.Arrow>
        {content}
      </Tooltip.Content>
    </Tooltip.Positioner>
  );

  return (
    <Tooltip.Root
      open={openOnClick ? open : undefined}
      onOpenChange={openOnClick ? (details) => {
        if (pinnedRef.current && !details.open) return;
        setOpen(details.open);
      } : undefined}
      closeOnClick={openOnClick ? false : undefined}
      closeOnPointerDown={openOnClick ? false : undefined}
      interactive={openOnClick ? true : undefined}
      openDelay={openDelay}
      closeDelay={closeDelay}
      positioning={{ placement, gutter: placement === 'right' ? 10 : placement === 'left' ? 10 : undefined }}
    >
      <Tooltip.Trigger asChild>
        {openOnClick ? (
          <chakra.button
            type="button"
            display="inline-flex"
            alignItems="center"
            bg="transparent"
            borderWidth={0}
            p={0}
            m={0}
            h="auto"
            minW={0}
            color="inherit"
            lineHeight="inherit"
            cursor="pointer"
            aria-expanded={open}
            onClick={(event) => {
              event.preventDefault();
              const next = !pinnedRef.current;
              pinnedRef.current = next;
              setOpen(next);
            }}
          >
            {children}
          </chakra.button>
        ) : children}
      </Tooltip.Trigger>
      {portal ? <Portal>{positioner}</Portal> : positioner}
    </Tooltip.Root>
  );
}
